/**
 * The general letter templates practitioners can issue from a patient chart
 * (PD-1407). Wording is taken from the template pack attached to the ticket —
 * keep edits in step with that pack rather than rewording here, since the letters
 * are patient-facing and were reviewed as written.
 *
 * Each template declares only the fields a practitioner must supply. Everything
 * derivable from the chart (patient name, date of birth, clinic, practitioner,
 * today's date) is filled in from `LetterContext` and never asked for.
 */

import {
  PURPOSE_OPTIONS,
  RECORD_TYPE_OPTIONS,
  SENSITIVE_TYPE_OPTIONS,
} from '../AuthorizationRelease/authorizationFormData'

/** Shown in place of a value the practitioner has not filled in yet. */
export const LETTER_BLANK = '__________'

const VERIFICATION_CLOSING =
  'Please contact our office if additional verification is required and the patient has authorized the release of further information.'

const SALUTATION = 'To Whom It May Concern:'

export type LetterFieldKind = 'date' | 'text' | 'time' | 'textarea' | 'select' | 'multiselect'

/** Separator for `multiselect` values, which are stored as one string. */
export const LETTER_MULTI_SEPARATOR = '; '

export function splitMultiValue(value?: string): string[] {
  return (value ?? '')
    .split(LETTER_MULTI_SEPARATOR)
    .map((item) => item.trim())
    .filter(Boolean)
}

export function joinMultiValue(values: string[]): string {
  return values.join(LETTER_MULTI_SEPARATOR)
}

/** How a field is prefilled when the letter is first opened. */
export type LetterFieldPrefill = 'today' | 'patientName' | 'oneYearFromToday'

/**
 * A constraint on one field, checked against the rest of the letter. Declared per
 * field so the same checks drive the inline error, the submit gate, and the
 * calendar's own min/max bounds — a letter must never be able to state that an
 * absence ended before it started, or that a patient was seen after the fact.
 */
export type LetterFieldRule =
  /** A date that describes something already done cannot be in the future. */
  | { kind: 'notFuture' }
  /** A date that has to still be ahead of us, e.g. an expiry. */
  | { kind: 'future' }
  | { kind: 'onOrAfter'; field: string }
  | { kind: 'after'; field: string }
  | { kind: 'onOrBefore'; field: string }
  /** Times, compared within the same day. */
  | { kind: 'timeAfter'; field: string }
  /** Must be filled in when another field holds one of these values. */
  | { kind: 'requiredWhen'; field: string; equals: readonly string[] }

export type LetterField = {
  name: string
  label: string
  kind: LetterFieldKind
  required?: boolean
  /** Options for `kind: 'select'`. The first option is not auto-selected. */
  options?: readonly string[]
  placeholder?: string
  prefill?: LetterFieldPrefill
  rules?: readonly LetterFieldRule[]
  /**
   * Only ask for this field while another field holds one of these values. A field
   * that does not apply is left out of the form altogether rather than accepted and
   * then complained about (PD-1575).
   */
  visibleWhen?: { field: string; equals: readonly string[] }
}

export type LetterCategory = 'Employment' | 'School' | 'Administrative' | 'Travel' | 'Activity'

/** Chart-derived values every letter can use without asking the practitioner. */
export type LetterContext = {
  patientName: string
  clinicName: string
}

export type LetterFieldValues = Record<string, string>

export type LetterTemplate = {
  /** Stable id, recorded on the saved document. Matches the template pack slug. */
  id: string
  title: string
  category: LetterCategory
  description: string
  fields: readonly LetterField[]
  /** Assembles the editable letter body. Unfilled values render as blanks. */
  buildBody: (values: LetterFieldValues, context: LetterContext) => string
  /** Adds blank patient signature lines to the PDF (see `LetterPdfInput`). */
  patientSignature?: boolean
}

/**
 * Formats an ISO `yyyy-mm-dd` as e.g. "August 6, 2026". Parsed into local parts
 * rather than via `new Date(iso)`, which reads the string as UTC midnight and
 * shifts the date back a day west of Greenwich.
 */
export function formatLetterDate(iso?: string): string {
  if (!iso) return LETTER_BLANK
  const [year, month, day] = iso.split('-').map(Number)
  if (!year || !month || !day) return LETTER_BLANK
  return new Date(year, month - 1, day).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

/**
 * Minutes since midnight for a practitioner-typed time, accepting the forms people
 * actually type: "9:00 AM", "9 am", "09:00", "14:30". Returns null when the text
 * isn't a time at all, which surfaces as an inline error rather than a letter that
 * claims an appointment ran from nonsense to nonsense.
 */
export function parseLetterTime(value?: string): number | null {
  const raw = value?.trim().toLowerCase().replace(/\./g, '')
  if (!raw) return null

  const match = /^(\d{1,2})(?::(\d{2}))?\s*(am|pm)?$/.exec(raw)
  if (!match) return null

  const hour = Number(match[1])
  const minute = match[2] ? Number(match[2]) : 0
  const meridiem = match[3]
  if (minute > 59) return null

  if (meridiem) {
    if (hour < 1 || hour > 12) return null
    const hour24 = (hour % 12) + (meridiem === 'pm' ? 12 : 0)
    return hour24 * 60 + minute
  }

  // No am/pm: read as 24-hour. A bare "9" is ambiguous, so require the minutes.
  if (hour > 23 || !match[2]) return null
  return hour * 60 + minute
}

/** Renders a parsed time back as "9:00 AM"; unparseable text is left as typed. */
export function formatLetterTime(value?: string): string {
  const minutes = parseLetterTime(value)
  if (minutes === null) return value?.trim() || LETTER_BLANK

  const hour24 = Math.floor(minutes / 60)
  const minute = minutes % 60
  const meridiem = hour24 >= 12 ? 'PM' : 'AM'
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12
  return `${hour12}:${String(minute).padStart(2, '0')} ${meridiem}`
}

function text(value?: string): string {
  const trimmed = value?.trim()
  return trimmed || LETTER_BLANK
}

/** Joins the paragraphs of a letter, dropping the ones that resolved to nothing. */
function letter(...paragraphs: (string | undefined)[]): string {
  return paragraphs.filter((paragraph): paragraph is string => Boolean(paragraph)).join('\n\n')
}

/**
 * A `Label: value` detail line. Omitted entirely when the practitioner left the
 * field empty, so an unused restriction never prints as a blank line.
 */
function detail(label: string, value?: string): string | undefined {
  const trimmed = value?.trim()
  return trimmed ? `${label}: ${trimmed}` : undefined
}

/** Groups detail lines into a single paragraph, or nothing if all were empty. */
function details(...lines: (string | undefined)[]): string | undefined {
  const present = lines.filter((line): line is string => Boolean(line))
  return present.length > 0 ? present.join('\n') : undefined
}

/** The Return-to-Work Clearance statuses that come with restrictions. */
const RESTRICTED_RETURN_STATUSES = [
  'Full duty with temporary restrictions',
  'Modified duty',
] as const

export const LETTER_TEMPLATES: readonly LetterTemplate[] = [
  {
    id: 'work_absence_letter',
    title: 'Work Absence Letter',
    category: 'Employment',
    description: 'Confirms that a patient was evaluated and supports an excused absence from work.',
    fields: [
      {
        name: 'visit_date',
        label: 'Visit date',
        kind: 'date',
        required: true,
        prefill: 'today',
        // The evaluation must have happened, and cannot follow the absence it excuses.
        // A visit part-way through the absence is fine (seen Wednesday, excused Mon–Fri).
        rules: [{ kind: 'notFuture' }, { kind: 'onOrBefore', field: 'absence_end_date' }],
      },
      { name: 'absence_start_date', label: 'Absence start', kind: 'date', required: true },
      {
        name: 'absence_end_date',
        label: 'Absence end',
        kind: 'date',
        required: true,
        rules: [{ kind: 'onOrAfter', field: 'absence_start_date' }],
      },
      {
        name: 'return_date',
        label: 'Anticipated return to work',
        kind: 'date',
        required: true,
        rules: [{ kind: 'after', field: 'absence_end_date' }],
      },
      {
        name: 'diagnosis',
        label: 'Diagnosis to disclose (optional)',
        kind: 'text',
        placeholder: 'Left blank, no diagnosis is disclosed',
      },
    ],
    buildBody: (values, context) =>
      letter(
        SALUTATION,
        `This letter confirms that ${context.patientName} was evaluated by ${context.clinicName} on ${formatLetterDate(values.visit_date)}.`,
        `Based on the clinical evaluation, the patient should be excused from work from ${formatLetterDate(values.absence_start_date)} through ${formatLetterDate(values.absence_end_date)}. The anticipated return-to-work date is ${formatLetterDate(values.return_date)}, provided symptoms allow.`,
        // Diagnosis disclosure is off unless the practitioner explicitly fills it in.
        values.diagnosis?.trim()
          ? `The relevant diagnosis is ${values.diagnosis.trim()}.`
          : undefined,
        VERIFICATION_CLOSING,
      ),
  },
  {
    id: 'return_to_work_clearance',
    title: 'Return-to-Work Clearance',
    category: 'Employment',
    description: 'Documents a practitioner-reviewed return to work, with or without restrictions.',
    fields: [
      {
        name: 'clearance_date',
        label: 'Clearance date',
        kind: 'date',
        required: true,
        prefill: 'today',
        rules: [{ kind: 'notFuture' }],
      },
      {
        name: 'return_date',
        label: 'Return to work effective',
        kind: 'date',
        required: true,
        rules: [{ kind: 'onOrAfter', field: 'clearance_date' }],
      },
      {
        name: 'return_status',
        label: 'Return status',
        kind: 'select',
        required: true,
        options: [
          'Full duty without restrictions',
          'Full duty with temporary restrictions',
          'Modified duty',
        ],
      },
      {
        name: 'restrictions',
        label: 'Temporary restrictions',
        kind: 'textarea',
        // Restrictions belong to a restricted return only: asked for when the status
        // implies them, absent from the form when it does not.
        visibleWhen: { field: 'return_status', equals: RESTRICTED_RETURN_STATUSES },
        // A restricted return with no restrictions listed says nothing.
        rules: [
          {
            kind: 'requiredWhen',
            field: 'return_status',
            equals: RESTRICTED_RETURN_STATUSES,
          },
        ],
      },
      {
        name: 'restriction_end_date',
        label: 'Restrictions through',
        kind: 'date',
        // Optional on purpose: restrictions with no end date are indefinite.
        visibleWhen: { field: 'return_status', equals: RESTRICTED_RETURN_STATUSES },
        rules: [{ kind: 'onOrAfter', field: 'return_date' }],
      },
    ],
    buildBody: (values, context) =>
      letter(
        SALUTATION,
        `${context.patientName} was evaluated by ${context.clinicName} and may return to work effective ${formatLetterDate(values.return_date)}.`,
        `This clearance reflects the patient’s status as of ${formatLetterDate(values.clearance_date)} and is subject to the limitations below.`,
        details(
          detail('Return status', values.return_status),
          detail('Temporary restrictions', values.restrictions),
          values.restriction_end_date
            ? detail('Restrictions through', formatLetterDate(values.restriction_end_date))
            : undefined,
        ),
        VERIFICATION_CLOSING,
      ),
  },
  {
    id: 'workplace_restrictions_letter',
    title: 'Temporary Workplace Restrictions',
    category: 'Employment',
    description:
      'Communicates temporary functional limitations without unnecessary diagnostic disclosure.',
    fields: [
      {
        name: 'effective_date',
        label: 'Effective date',
        kind: 'date',
        required: true,
        prefill: 'today',
      },
      {
        name: 'restriction_end_date',
        label: 'Restrictions through',
        kind: 'date',
        required: true,
        rules: [{ kind: 'onOrAfter', field: 'effective_date' }],
      },
      {
        name: 'restrictions',
        label: 'Functional restrictions',
        kind: 'textarea',
        required: true,
        placeholder: 'e.g. No lifting over 10 lbs; no prolonged standing',
      },
      { name: 'recommended_accommodations', label: 'Recommended accommodations', kind: 'textarea' },
    ],
    buildBody: (values, context) =>
      letter(
        SALUTATION,
        `${context.patientName} is under medical care and has temporary functional limitations that may affect work duties.`,
        `The following restrictions and accommodations are recommended from ${formatLetterDate(values.effective_date)} through ${formatLetterDate(values.restriction_end_date)}. These recommendations should be reassessed if the patient’s condition changes.`,
        details(
          detail('Functional restrictions', values.restrictions),
          detail('Recommended accommodations', values.recommended_accommodations),
        ),
        VERIFICATION_CLOSING,
      ),
  },
  {
    id: 'school_absence_letter',
    title: 'School Absence Letter',
    category: 'School',
    description: 'Confirms a medical evaluation and supports an excused school absence.',
    fields: [
      {
        name: 'student_name',
        label: 'Student name',
        kind: 'text',
        required: true,
        prefill: 'patientName',
      },
      {
        name: 'visit_date',
        label: 'Visit date',
        kind: 'date',
        required: true,
        prefill: 'today',
        rules: [{ kind: 'notFuture' }, { kind: 'onOrBefore', field: 'absence_end_date' }],
      },
      { name: 'absence_start_date', label: 'Absence start', kind: 'date', required: true },
      {
        name: 'absence_end_date',
        label: 'Absence end',
        kind: 'date',
        required: true,
        rules: [{ kind: 'onOrAfter', field: 'absence_start_date' }],
      },
      {
        name: 'return_date',
        label: 'May return on',
        kind: 'date',
        required: true,
        rules: [{ kind: 'after', field: 'absence_end_date' }],
      },
    ],
    buildBody: (values, context) =>
      letter(
        SALUTATION,
        `This letter confirms that ${text(values.student_name)} was evaluated by ${context.clinicName} on ${formatLetterDate(values.visit_date)}.`,
        `Please excuse the student from school from ${formatLetterDate(values.absence_start_date)} through ${formatLetterDate(values.absence_end_date)}. The student may return on ${formatLetterDate(values.return_date)}, provided symptoms allow.`,
        VERIFICATION_CLOSING,
      ),
  },
  {
    id: 'return_to_school_clearance',
    title: 'Return-to-School Clearance',
    category: 'School',
    description:
      'Documents a practitioner-reviewed return to school and any temporary limitations.',
    fields: [
      {
        name: 'student_name',
        label: 'Student name',
        kind: 'text',
        required: true,
        prefill: 'patientName',
      },
      { name: 'return_date', label: 'Return to school effective', kind: 'date', required: true },
      { name: 'school_restrictions', label: 'School limitations', kind: 'textarea' },
      {
        name: 'restriction_end_date',
        label: 'Limitations through',
        kind: 'date',
        rules: [{ kind: 'onOrAfter', field: 'return_date' }],
      },
    ],
    buildBody: (values) =>
      letter(
        SALUTATION,
        `${text(values.student_name)} was evaluated and may return to school effective ${formatLetterDate(values.return_date)}.`,
        'The student may participate in regular academic activities subject to the temporary limitations listed below.',
        details(
          detail('School limitations', values.school_restrictions),
          values.restriction_end_date
            ? detail('Limitations through', formatLetterDate(values.restriction_end_date))
            : undefined,
        ),
        VERIFICATION_CLOSING,
      ),
  },
  {
    id: 'school_pe_activity_restriction',
    title: 'School / PE Activity Restriction',
    category: 'School',
    description: 'Communicates temporary school, PE, recess, or sports restrictions.',
    fields: [
      {
        name: 'student_name',
        label: 'Student name',
        kind: 'text',
        required: true,
        prefill: 'patientName',
      },
      {
        name: 'effective_date',
        label: 'Effective date',
        kind: 'date',
        required: true,
        prefill: 'today',
      },
      {
        name: 'restriction_end_date',
        label: 'Limitations through',
        kind: 'date',
        required: true,
        rules: [{ kind: 'onOrAfter', field: 'effective_date' }],
      },
      {
        name: 'restricted_activities',
        label: 'Restricted activities',
        kind: 'textarea',
        required: true,
        placeholder: 'e.g. No contact sports, no running',
      },
      { name: 'permitted_activities', label: 'Permitted activities', kind: 'textarea' },
    ],
    buildBody: (values) =>
      letter(
        SALUTATION,
        `${text(values.student_name)} has been evaluated and requires temporary modification of physical activity at school.`,
        `These limitations apply from ${formatLetterDate(values.effective_date)} through ${formatLetterDate(values.restriction_end_date)}, unless revised earlier by the treating practitioner.`,
        details(
          detail('Restricted activities', values.restricted_activities),
          detail('Permitted activities', values.permitted_activities),
        ),
        VERIFICATION_CLOSING,
      ),
  },
  {
    id: 'medical_appointment_verification',
    title: 'Medical Appointment Verification',
    category: 'Administrative',
    description: 'Verifies attendance at an appointment without disclosing clinical information.',
    fields: [
      {
        name: 'visit_date',
        label: 'Appointment date',
        kind: 'date',
        required: true,
        prefill: 'today',
        rules: [{ kind: 'notFuture' }],
      },
      { name: 'appointment_start_time', label: 'Start time', kind: 'time', required: true },
      {
        name: 'appointment_end_time',
        label: 'End time',
        kind: 'time',
        required: true,
        rules: [{ kind: 'timeAfter', field: 'appointment_start_time' }],
      },
      {
        name: 'attendance_mode',
        label: 'Attendance mode',
        kind: 'select',
        required: true,
        options: ['in-person visit', 'telehealth video visit', 'telephone visit'],
      },
    ],
    buildBody: (values, context) =>
      letter(
        SALUTATION,
        `This letter verifies that ${context.patientName} attended a medical appointment with ${context.clinicName} on ${formatLetterDate(values.visit_date)}.`,
        `The appointment occurred from approximately ${formatLetterTime(values.appointment_start_time)} to ${formatLetterTime(values.appointment_end_time)} and was completed by ${text(values.attendance_mode)}.`,
        'No diagnosis or treatment information is included in this attendance verification.',
        VERIFICATION_CLOSING,
      ),
  },
  {
    // A deliberately *general* authorization: the chart's existing "Authorization /
    // Release" flow (PD-920) targets one named facility, which is no use when the
    // patient doesn't know who holds their records. Same authorized-release content,
    // addressed to whoever holds them. The patient signs it, so the PDF carries
    // blank patient signature lines.
    id: 'general_records_authorization',
    title: 'Authorization to Request Records',
    category: 'Administrative',
    description:
      'A general authorization the patient signs so any provider holding their records can release them to us.',
    patientSignature: true,
    fields: [
      {
        name: 'records_requested',
        label: 'Records authorized for release',
        kind: 'multiselect',
        required: true,
        // 'Other' is dropped — the free-text field below covers it.
        options: RECORD_TYPE_OPTIONS.filter((option) => option !== 'Other'),
      },
      {
        name: 'additional_records',
        label: 'Additional records',
        kind: 'text',
        placeholder: 'Anything not covered by the list above',
      },
      {
        name: 'date_range_start',
        label: 'Records from',
        kind: 'date',
        rules: [{ kind: 'notFuture' }],
      },
      {
        name: 'date_range_end',
        label: 'Records to',
        kind: 'date',
        rules: [{ kind: 'notFuture' }, { kind: 'onOrAfter', field: 'date_range_start' }],
      },
      {
        name: 'purpose',
        label: 'Purpose of disclosure',
        kind: 'select',
        required: true,
        options: PURPOSE_OPTIONS,
      },
      {
        name: 'purpose_other',
        label: 'Purpose (if other)',
        kind: 'text',
        rules: [{ kind: 'requiredWhen', field: 'purpose', equals: ['Other'] }],
      },
      {
        name: 'sensitive_types',
        label: 'Sensitive information specifically authorized',
        kind: 'multiselect',
        // Nothing is authorized unless the practitioner and patient choose it.
        options: SENSITIVE_TYPE_OPTIONS,
      },
      {
        name: 'expiration_date',
        label: 'Authorization expires on',
        kind: 'date',
        required: true,
        prefill: 'oneYearFromToday',
        // An authorization that expires today (or earlier) is already spent.
        rules: [{ kind: 'future' }],
      },
    ],
    buildBody: (values, context) => {
      const purpose = values.purpose === 'Other' ? text(values.purpose_other) : text(values.purpose)
      const dateRange =
        values.date_range_start || values.date_range_end
          ? `${formatLetterDate(values.date_range_start)} through ${formatLetterDate(values.date_range_end)}`
          : 'All available records'

      return letter(
        SALUTATION,
        `${context.clinicName} is requesting the medical records of ${context.patientName} for continuity of care. The patient has signed the authorization below, which applies to any healthcare provider, facility, laboratory, imaging center, hospital, pharmacy, or health information exchange holding their records — it is general and is not limited to a single provider or facility.`,
        details(
          detail('Records authorized for release', values.records_requested),
          detail('Additional records', values.additional_records),
          detail('Date range of records requested', dateRange),
          detail('Purpose of disclosure', purpose),
          detail('Sensitive information specifically authorized', values.sensitive_types),
          detail('This authorization expires on', formatLetterDate(values.expiration_date)),
        ),
        'The patient understands that this authorization may be revoked at any time in writing, except to the extent action has already been taken in reliance upon it; that treatment, payment, enrollment, or eligibility for benefits may not be conditioned upon signing it unless permitted by law; and that information disclosed under it may be subject to redisclosure by the recipient and may no longer be protected by HIPAA.',
        `Please release the records to ${context.clinicName} using the fax number or address shown above.`,
        VERIFICATION_CLOSING,
      )
    },
  },
  {
    id: 'travel_postponement_letter',
    title: 'Medical Travel Postponement Letter',
    category: 'Travel',
    description: 'Supports a clinician-determined request to postpone or cancel travel.',
    fields: [
      {
        name: 'evaluation_date',
        label: 'Evaluation date',
        kind: 'date',
        required: true,
        prefill: 'today',
        rules: [{ kind: 'notFuture' }],
      },
      { name: 'travel_start_date', label: 'Travel start', kind: 'date', required: true },
      {
        name: 'travel_end_date',
        label: 'Travel end',
        kind: 'date',
        required: true,
        // Travel already finished before the assessment can't be "not recommended".
        rules: [
          { kind: 'onOrAfter', field: 'travel_start_date' },
          { kind: 'onOrAfter', field: 'evaluation_date' },
        ],
      },
      {
        name: 'travel_recommendation',
        label: 'Recommendation',
        kind: 'select',
        required: true,
        options: [
          'postponement of travel',
          'cancellation of travel',
          'deferral of travel pending reassessment',
        ],
      },
      {
        name: 'reassessment_date',
        label: 'May be reassessed on or after',
        kind: 'date',
        rules: [{ kind: 'onOrAfter', field: 'evaluation_date' }],
      },
    ],
    buildBody: (values, context) =>
      letter(
        SALUTATION,
        `${context.patientName} was evaluated on ${formatLetterDate(values.evaluation_date)}. Based on the current clinical assessment, travel scheduled from ${formatLetterDate(values.travel_start_date)} through ${formatLetterDate(values.travel_end_date)} is not recommended.`,
        `The current recommendation is: ${text(values.travel_recommendation)}.${
          values.reassessment_date
            ? ` The patient may be reassessed on or after ${formatLetterDate(values.reassessment_date)}.`
            : ''
        }`,
        'This letter is provided at the patient’s request. Any refund, credit, or waiver remains subject to the recipient organization’s policies.',
        VERIFICATION_CLOSING,
      ),
  },
  {
    id: 'jury_duty_medical_excuse',
    title: 'Jury Duty Medical Excuse',
    category: 'Administrative',
    description: 'Supports a practitioner-reviewed request for temporary deferral or excusal.',
    fields: [
      {
        name: 'evaluation_date',
        label: 'Evaluation date',
        kind: 'date',
        required: true,
        prefill: 'today',
        rules: [{ kind: 'notFuture' }],
      },
      {
        name: 'service_date',
        label: 'Jury service begins',
        kind: 'date',
        required: true,
        rules: [{ kind: 'onOrAfter', field: 'evaluation_date' }],
      },
      {
        name: 'jury_recommendation',
        label: 'Recommendation',
        kind: 'select',
        required: true,
        options: ['temporary deferral', 'excusal from service'],
      },
      {
        name: 'expected_duration',
        label: 'Expected duration',
        kind: 'text',
        required: true,
        placeholder: 'e.g. 6 weeks',
      },
    ],
    buildBody: (values, context) =>
      letter(
        SALUTATION,
        `${context.patientName} was evaluated on ${formatLetterDate(values.evaluation_date)} and currently has a medical limitation affecting the ability to participate in jury service beginning ${formatLetterDate(values.service_date)}.`,
        `The clinical recommendation is ${text(values.jury_recommendation)} for an expected duration of ${text(values.expected_duration)}.`,
        'This letter provides medical support for the request; the final determination rests with the court.',
        VERIFICATION_CLOSING,
      ),
  },
  {
    id: 'activity_sports_clearance',
    title: 'Activity / Sports Clearance',
    category: 'Activity',
    description: 'Documents a practitioner-reviewed return to exercise or sports.',
    fields: [
      {
        name: 'evaluation_date',
        label: 'Evaluation date',
        kind: 'date',
        required: true,
        prefill: 'today',
        rules: [{ kind: 'notFuture' }],
      },
      {
        name: 'clearance_date',
        label: 'Clearance effective',
        kind: 'date',
        required: true,
        rules: [{ kind: 'onOrAfter', field: 'evaluation_date' }],
      },
      {
        name: 'activity_status',
        label: 'Participation status',
        kind: 'select',
        required: true,
        options: [
          'Cleared for full participation',
          'Cleared with restrictions',
          'Not cleared at this time',
        ],
      },
      {
        name: 'permitted_activities',
        label: 'Permitted activities',
        kind: 'textarea',
        // Nothing is permitted while the patient is not cleared at all.
        visibleWhen: {
          field: 'activity_status',
          equals: ['Cleared for full participation', 'Cleared with restrictions'],
        },
      },
      {
        name: 'restrictions',
        label: 'Restrictions',
        kind: 'textarea',
        // A full clearance has no restrictions to state; anything less does.
        visibleWhen: {
          field: 'activity_status',
          equals: ['Cleared with restrictions', 'Not cleared at this time'],
        },
        rules: [
          { kind: 'requiredWhen', field: 'activity_status', equals: ['Cleared with restrictions'] },
        ],
      },
      {
        name: 'follow_up_date',
        label: 'Follow-up date',
        kind: 'date',
        rules: [{ kind: 'after', field: 'clearance_date' }],
      },
    ],
    buildBody: (values, context) =>
      letter(
        SALUTATION,
        `${context.patientName} was evaluated on ${formatLetterDate(values.evaluation_date)} for readiness to participate in physical activity.`,
        `Effective ${formatLetterDate(values.clearance_date)}, the patient’s participation status is recorded below. This clearance applies only to the information available at the time of evaluation.`,
        details(
          detail('Participation status', values.activity_status),
          detail('Permitted activities', values.permitted_activities),
          detail('Restrictions', values.restrictions),
          values.follow_up_date
            ? detail('Follow-up date', formatLetterDate(values.follow_up_date))
            : undefined,
        ),
        VERIFICATION_CLOSING,
      ),
  },
]

export const LETTER_CATEGORY_ORDER: readonly LetterCategory[] = [
  'Employment',
  'School',
  'Administrative',
  'Travel',
  'Activity',
]

export function findLetterTemplate(id: string): LetterTemplate | undefined {
  return LETTER_TEMPLATES.find((template) => template.id === id)
}

/** ISO `yyyy-mm-dd` for today in the practitioner's own timezone. */
export function todayIso(): string {
  const now = new Date()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return `${now.getFullYear()}-${month}-${day}`
}

/** A year out, the usual shelf life for a records authorization. */
export function oneYearFromTodayIso(): string {
  const now = new Date()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return `${now.getFullYear() + 1}-${month}-${day}`
}

/** Initial field values for a template: prefilled from the chart where possible. */
export function buildInitialValues(
  template: LetterTemplate,
  context: LetterContext,
): LetterFieldValues {
  const values: LetterFieldValues = {}
  for (const field of template.fields) {
    if (field.prefill === 'today') values[field.name] = todayIso()
    else if (field.prefill === 'oneYearFromToday') values[field.name] = oneYearFromTodayIso()
    else if (field.prefill === 'patientName') values[field.name] = context.patientName
    else values[field.name] = ''
  }
  return values
}

/** Whether a field applies to the letter as it currently stands. */
export function isLetterFieldVisible(field: LetterField, values: LetterFieldValues): boolean {
  if (!field.visibleWhen) return true
  return field.visibleWhen.equals.includes(values[field.visibleWhen.field]?.trim() ?? '')
}

/**
 * The fields to ask for, in declaration order. A hidden field is not part of the
 * letter: it is neither rendered, nor required, nor validated.
 */
export function visibleLetterFields(
  template: LetterTemplate,
  values: LetterFieldValues,
): readonly LetterField[] {
  if (!template.fields.some((field) => field.visibleWhen)) return template.fields
  return template.fields.filter((field) => isLetterFieldVisible(field, values))
}

/**
 * Whether a field must be filled in as the letter currently stands — either always,
 * or because another field's value has made it required.
 */
export function isLetterFieldRequired(field: LetterField, values: LetterFieldValues): boolean {
  if (field.required) return true
  return (field.rules ?? []).some(
    (rule) =>
      rule.kind === 'requiredWhen' && rule.equals.includes(values[rule.field]?.trim() ?? ''),
  )
}

/** Names of the required fields the practitioner still has to fill in. */
export function missingRequiredFields(
  template: LetterTemplate,
  values: LetterFieldValues,
): string[] {
  return visibleLetterFields(template, values)
    .filter((field) => field.required && !values[field.name]?.trim())
    .map((field) => field.label)
}

function labelOf(template: LetterTemplate, fieldName: string): string {
  return template.fields.find((field) => field.name === fieldName)?.label ?? fieldName
}

function addDays(iso: string, days: number): string {
  const [year, month, day] = iso.split('-').map(Number)
  const date = new Date(year, month - 1, day + days)
  const nextMonth = String(date.getMonth() + 1).padStart(2, '0')
  const nextDay = String(date.getDate()).padStart(2, '0')
  return `${date.getFullYear()}-${nextMonth}-${nextDay}`
}

/**
 * Checks one field's rules against the whole letter. Rules that reference a field
 * the practitioner hasn't filled in yet stay quiet — the missing-value complaint
 * belongs to the required check, not here.
 */
function fieldError(
  template: LetterTemplate,
  field: LetterField,
  values: LetterFieldValues,
): string | undefined {
  const value = values[field.name]?.trim() ?? ''

  for (const rule of field.rules ?? []) {
    const other = 'field' in rule ? (values[rule.field]?.trim() ?? '') : ''

    switch (rule.kind) {
      case 'notFuture':
        if (value && value > todayIso()) return `${field.label} cannot be in the future`
        break
      case 'future':
        if (value && value <= todayIso()) return `${field.label} must be a future date`
        break
      case 'onOrAfter':
        if (value && other && value < other)
          return `${field.label} must be on or after ${labelOf(template, rule.field)}`
        break
      case 'after':
        if (value && other && value <= other)
          return `${field.label} must be after ${labelOf(template, rule.field)}`
        break
      case 'onOrBefore':
        if (value && other && value > other)
          return `${field.label} must be on or before ${labelOf(template, rule.field)}`
        break
      case 'timeAfter': {
        const parsed = parseLetterTime(value)
        if (value && parsed === null) return `Enter a time like 9:00 AM`
        const parsedOther = parseLetterTime(other)
        if (parsed !== null && parsedOther !== null && parsed <= parsedOther)
          return `${field.label} must be after ${labelOf(template, rule.field)}`
        break
      }
      case 'requiredWhen':
        if (!value && rule.equals.includes(other))
          return `${field.label} is required when ${labelOf(template, rule.field)} is “${other}”`
        break
    }
  }

  // A time field with no ordering rule of its own still has to be a time.
  if (field.kind === 'time' && value && parseLetterTime(value) === null) {
    return `Enter a time like 9:00 AM`
  }

  return undefined
}

/**
 * Every rule violation in the letter, keyed by field name, so the form can mark the
 * offending inputs and the submit can be refused.
 */
export function letterFieldErrors(
  template: LetterTemplate,
  values: LetterFieldValues,
): Record<string, string> {
  const errors: Record<string, string> = {}
  for (const field of visibleLetterFields(template, values)) {
    const error = fieldError(template, field, values)
    if (error) errors[field.name] = error
  }
  return errors
}

/**
 * The range a date field's calendar should allow, derived from the same rules that
 * produce the errors — so an impossible date is hard to pick in the first place.
 */
export function letterDateBounds(
  field: LetterField,
  values: LetterFieldValues,
): { minDate?: string; maxDate?: string } {
  let minDate: string | undefined
  let maxDate: string | undefined

  const tighterMin = (candidate?: string) => {
    if (candidate && (!minDate || candidate > minDate)) minDate = candidate
  }
  const tighterMax = (candidate?: string) => {
    if (candidate && (!maxDate || candidate < maxDate)) maxDate = candidate
  }

  for (const rule of field.rules ?? []) {
    const other = 'field' in rule ? values[rule.field]?.trim() : undefined
    switch (rule.kind) {
      case 'notFuture':
        tighterMax(todayIso())
        break
      case 'future':
        tighterMin(addDays(todayIso(), 1))
        break
      case 'onOrAfter':
        tighterMin(other)
        break
      case 'after':
        tighterMin(other ? addDays(other, 1) : undefined)
        break
      case 'onOrBefore':
        tighterMax(other)
        break
      default:
        break
    }
  }

  return { minDate, maxDate }
}
